/**
 * Página de Ayuda del portal vecino (link 'Ayuda' del menú).
 *
 * Dos bloques, ambos en acordeón nativo (`AcordeonItem`, mobile-first —
 * ~80% del acceso del barrio es por celular):
 *  1. **Guía por página:** qué ves y qué haces en cada pantalla del portal.
 *  2. **Preguntas frecuentes (FAQ):** dudas puntuales del flujo (F14).
 *
 * Lenguaje usuario-facing: "casa" (no lote), "Comisión" (no directiva).
 */
import { useState } from 'react';
import { DatosCuentaModal } from '../components/ui/DatosCuentaModal';
import { AcordeonItem } from '../components/ui/AcordeonItem';

export function Ayuda() {
  const [datosCuentaAbierto, setDatosCuentaAbierto] = useState(false);

  return (
    <div className="max-w-2xl py-8">
      <h2 className="text-xl font-medium text-text-primary mb-1">Ayuda</h2>
      <p className="text-sm text-text-muted mb-6">
        Toca cada título para ver el detalle.
      </p>

      {/* ============ GUÍA POR PÁGINA ============ */}
      <h3 className="text-base font-medium text-text-primary mb-1">
        Guía por página
      </h3>
      <p className="text-sm text-text-muted mb-3">
        Qué encuentras en cada pantalla del portal.
      </p>

      <AcordeonItem titulo="Inicio">
        <p>
          Es tu resumen al entrar: el estado de tu casa, tu próxima cuota y
          accesos rápidos a las demás secciones. Desde aquí navegas a todo lo
          demás (en el celular, con la barra de abajo).
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="Mi cuenta — tus cuotas y pagos">
        <p>
          Muestra el estado de cuenta de tu casa: las <strong>cuotas</strong>{' '}
          del año (pagadas, pendientes, en abono), tus <strong>pagos</strong>{' '}
          registrados, si estás <strong>al día o en mora</strong>, y tu{' '}
          <strong>saldo a favor</strong> si pagaste de más (se aplica a tus
          próximas cuotas).
        </p>
        <p className="text-text-muted">
          Incluye <strong>Cómo pagar</strong> con los datos de transferencia
          del barrio. Tras transferir, tu pago se registra solo si pusiste tu
          casa en el mensaje (ver la FAQ "¿Cómo pago mi cuota?").
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="Mis datos — tu información, tu hogar y tus recursos">
        <p>
          Es tu pantalla principal de gestión, con <strong>3 pestañas</strong>:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Mis datos:</strong> editas tu email y celular (el nombre y
            RUT los administra la Comisión).
          </li>
          <li>
            <strong>Residentes:</strong> registras a quienes viven contigo
            (familia, arrendatarios) y configuras su citofonía/WiFi.
          </li>
          <li>
            <strong>Vehículos:</strong> registras tus autos por patente
            (seguridad + catastro de Bomberos).
          </li>
        </ul>
        <p className="text-text-muted">
          Dentro de la pestaña <strong>Mis datos</strong>, más abajo, están los{' '}
          <strong>Recursos de tu casa</strong> (llaveros y controles: repartes
          los sin asignar y reportas si uno falla) y, al final,{' '}
          <strong>"Dejé el barrio"</strong> (si vendiste o terminó tu arriendo)
          y <strong>"Mis derechos"</strong> sobre tus datos (Ley 21.719).
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="Documentos">
        <p>
          Los documentos que publica la Comisión: reglamento, actas,
          instructivos. Solo lectura.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="Avisos">
        <p>
          Los comunicados de la Comisión al barrio (reuniones, cortes,
          recordatorios). También te llegan por correo.
        </p>
      </AcordeonItem>

      {/* ============ PREGUNTAS FRECUENTES ============ */}
      <h3 className="text-base font-medium text-text-primary mt-8 mb-1">
        Preguntas frecuentes
      </h3>
      <p className="text-sm text-text-muted mb-3">
        Dudas puntuales del día a día.
      </p>

      {/* === Acceso === */}
      <AcordeonItem titulo="¿Cómo entro al portal?">
        <p>
          Usamos enlaces por correo, no contraseñas. Escribe tu email en la
          pantalla de inicio y te llega un enlace para entrar. Caduca en una
          hora y se usa una sola vez.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="No me llega el correo para entrar">
        <p>
          Revisa la carpeta de spam o promociones. Si tu correo no está en el
          sistema, completa la <strong>solicitud de acceso</strong> en la
          pantalla de inicio: la Comisión la revisa y, al aprobarla, podrás
          entrar.
        </p>
      </AcordeonItem>

      {/* === Pago === */}
      <AcordeonItem titulo="¿Cómo pago mi cuota?">
        <p>
          El pago es por transferencia a la cuenta del barrio.{' '}
          <button
            type="button"
            onClick={() => setDatosCuentaAbierto(true)}
            className="text-orange hover:underline"
          >
            Abre los datos para transferir
          </button>{' '}
          (también en <strong>Mi Cuenta → Cómo pagar</strong>).
        </p>
        <p className="text-text-muted">
          Importante: en el mensaje de la transferencia escribe tu casa y el
          motivo (ej. <span className="font-mono">Casa 1234, cuota</span>). Así
          tu pago se registra solo; si falta ese dato, la Comisión tiene que
          asignarlo a mano y demora.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="¿Qué pasa si me atraso en la cuota?">
        <p>
          Una cuota vencida te deja en mora. El barrio puede{' '}
          <strong>suspender accesos</strong> (citofonía, acceso WiFi) mientras
          estés atrasado. Al ponerte al día, se reactivan.
        </p>
      </AcordeonItem>

      {/* === Mis datos / familia / autos === */}
      <AcordeonItem titulo="¿Cómo actualizo mis datos?">
        <p>
          En <strong>Mis Datos</strong> editas tu email y celular. El nombre y
          el RUT los administra la Comisión: si hay un error, escríbeles.
        </p>
        <p className="text-text-muted">
          Tu celular habilita la citofonía (la llamada) y tu email el acceso
          WiFi. Sin ellos, te quedas sin esos servicios.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="¿Cómo registro a quienes viven conmigo?">
        <p>
          En <strong>Mis Datos → Residentes</strong> agregas a tu familia o
          arrendatarios. El objetivo es que el barrio sepa quién vive en cada
          casa.
        </p>
        <p className="text-text-muted">
          Cada residente nuevo queda "pendiente de validación" hasta que la
          Comisión lo aprueba. Con su celular/email podrá tener citofonía/WiFi.
        </p>
        <p className="text-text-muted">
          Para el <strong>WiFi</strong>, ese correo necesita tener una cuenta
          de <strong>eWeLink</strong> creada con esa misma dirección. Es
          gratis y se crea desde la aplicación eWeLink. Si no existe, el
          acceso no se puede activar y después de varios intentos te avisamos
          por correo.
        </p>
        <p className="text-text-muted">
          Si <strong>editas</strong> a un residente ya aprobado, el sistema te
          pide una <strong>justificación del cambio</strong>: como vuelve a
          pasar por la Comisión, ese texto es lo que le explica qué cambió y por
          qué. Sin él no se puede guardar, y el botón te dice qué falta.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="¿Por qué registro mis vehículos?">
        <p>
          En <strong>Mis Datos → Vehículos</strong> registras tus autos por
          patente. Sirve para la seguridad del barrio y, sobre todo, para el{' '}
          <strong>catastro de Bomberos</strong>: ante una emergencia necesitan
          saber qué vehículos tienen batería (híbrido/eléctrico).
        </p>
        <p className="text-text-muted">
          La patente no se puede editar después. Si cambias de auto, da de baja
          el anterior y registra el nuevo.
        </p>
      </AcordeonItem>

      {/* === Recursos === */}
      <AcordeonItem titulo="¿Cómo asigno llaveros y controles remotos?">
        <p>
          En <strong>Mis Datos → Recursos</strong>, si tu casa tiene llaveros o
          controles sin asignar (en la "bolsa"), los repartes: el llavero a una
          persona, el control a un vehículo. Verifica el llavero por su número
          de serie.
        </p>
        <p className="text-text-muted">
          Asignar de la bolsa es inmediato. Si no queda ninguno y necesitas más,
          usa "Solicitar recurso": va a la Comisión y puede tener costo.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="Cada persona lleva un llavero, cada auto un control">
        <p>
          Un llavero pertenece a <strong>una sola persona</strong>: es su
          identificación en el portón. Por eso, cuando reparte los llaveros de
          la bolsa, quien ya tiene el suyo deja de aparecer en la lista.
        </p>
        <p>
          Con los controles remotos es distinto: el control abre la barrera para
          un <strong>auto</strong> específico, así que lo que no se repite es el
          auto. Si tienes dos autos, llevas los dos controles.
        </p>
        <p className="text-text-muted">
          Si te sobran llaveros en la bolsa, es porque falta registrar a quien
          los usa. Agrega a esa persona en Mis Datos → Residentes y después
          asígnale el llavero: así el barrio sabe quién entra con cada uno, que
          es lo que permite reprogramar el portón cuando hace falta.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="¿Cómo configuro la citofonía y el WiFi?">
        <p>
          En <strong>Mis Datos</strong>, por cada residente. La citofonía llama
          al residente (necesita su celular); el WiFi le da acceso (necesita su
          email).
        </p>
        <p className="text-text-muted">
          Cada cambio pasa por validación de la Comisión, porque alguien debe
          reflejarlo en el equipo físico.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="Se me perdió o falló un llavero/control">
        <p>
          En <strong>Mis Datos → Recursos</strong>, usa "Reportar problema" en
          el recurso. La Comisión evalúa el reemplazo, que puede tener costo de
          reposición.
        </p>
        <p className="text-text-muted">
          Es importante hacerlo por ahí y no con "Solicitar recurso": al
          reportarlo, el barrio da de baja el llavero perdido y te entrega el
          nuevo en su lugar. Si pidieras uno suelto, quedarían dos a tu nombre y
          el sistema no lo permite.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="Alguien se fue de la casa, o vendí el auto">
        <p>
          Cuando das de baja a una persona en{' '}
          <strong>Mis Datos → Residentes</strong>, su llavero vuelve solo a la
          bolsa de tu casa. Queda disponible para asignárselo a quien llegue.
        </p>
        <p>
          Lo mismo con los autos: si das de baja un vehículo, su control remoto
          vuelve a la bolsa y puedes asignarlo a tu auto nuevo. No necesitas
          pedir otro ni pagarlo de nuevo.
        </p>
      </AcordeonItem>

      {/* === Salida / derechos === */}
      <AcordeonItem titulo="Vendí mi casa o terminó mi arriendo, ¿qué hago?">
        <p>
          En <strong>Mis Datos</strong>, al final, usa "Dejé el barrio". Cierra
          tu vínculo con la casa y la Comisión baja tus accesos (citofonía y
          WiFi).
        </p>
        <p className="text-text-muted">
          Tus datos se conservan solo para lo contable e histórico; dejas de
          recibir comunicaciones del barrio.
        </p>
      </AcordeonItem>

      <AcordeonItem titulo="¿Qué derechos tengo sobre mis datos (Ley 21.719)?">
        <p>
          En <strong>Mis Datos → Mis derechos</strong> puedes descargar una
          copia de tus datos o solicitar la eliminación de tu cuenta.
        </p>
        <p className="text-text-muted">
          La eliminación requiere que primero hayas dejado todas tus casas; la
          Comisión anonimiza tus datos conservando solo lo contable, sin
          identificarte.
        </p>
      </AcordeonItem>

      {/* === Contacto === */}
      <AcordeonItem titulo="Necesito ayuda con otra cosa">
        <p>
          Escribe a la Comisión a{' '}
          <a
            href="mailto:administracion@calbucura.cl"
            className="text-orange hover:underline"
          >
            administracion@calbucura.cl
          </a>
          .
        </p>
      </AcordeonItem>

      {datosCuentaAbierto && (
        <DatosCuentaModal onClose={() => setDatosCuentaAbierto(false)} />
      )}
    </div>
  );
}
